import { ExternalLink, Target } from "lucide-react";

import { AsideSection } from "../misc/AsideSection";
import type { Company } from "../types";

// LDF: Akquise-Bewertung aus der Lead-Recherche an der Firma.

const SCORE_STYLE: Record<string, { label: string; className: string }> = {
  hot: { label: "Hot", className: "bg-primary text-primary-foreground" },
  warm: { label: "Warm", className: "bg-secondary text-secondary-foreground" },
  cold: { label: "Cold", className: "bg-muted text-muted-foreground" },
  skip: { label: "Skip", className: "bg-muted text-muted-foreground line-through" },
};

export const LeadBadge = ({ score }: { score?: string | null }) => {
  if (!score || !SCORE_STYLE[score]) return null;
  const style = SCORE_STYLE[score];
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium leading-none ${style.className}`}>
      {style.label}
    </span>
  );
};

const Row = ({ label, value }: { label: string; value?: string | null }) =>
  value && value !== "–" ? (
    <div className="text-sm">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <span className="whitespace-pre-line break-words">{value}</span>
    </div>
  ) : null;

export const CompanyLead = ({ record }: { record: Company }) => {
  if (!record.lead_score) return null;
  return (
    <AsideSection title="Akquise">
      <div className="flex items-center gap-2">
        <Target className="h-4 w-4" />
        <LeadBadge score={record.lead_score} />
        {record.lead_segment && <span className="text-xs text-muted-foreground">{record.lead_segment}</span>}
      </div>
      <Row label="Warum passt die Firma" value={record.lead_reason} />
      <Row label="Beleg / Signal" value={record.lead_signal} />
      <Row label="Nächster Anlass" value={record.next_event} />
      <Row label="Ansprechrolle" value={record.contact_role} />
      <Row label="Empfohlener Kanal" value={record.outreach_channel} />
      {record.lead_sources?.length ? (
        <div className="text-sm">
          <span className="block text-xs text-muted-foreground">Quellen</span>
          {record.lead_sources.map((url) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 underline hover:no-underline truncate"
              title={url}
            >
              <ExternalLink className="h-3 w-3 shrink-0" />
              <span className="truncate">{url.replace(/^https?:\/\/(www\.)?/, "")}</span>
            </a>
          ))}
        </div>
      ) : null}
      {(record.lead_confidence || record.lead_verified_date) && (
        <p className="text-xs text-muted-foreground">
          Recherche: {record.lead_confidence ?? "–"}
          {record.lead_verified_date ? `, geprüft ${new Date(record.lead_verified_date).toLocaleDateString("de-DE")}` : ""}
        </p>
      )}
    </AsideSection>
  );
};
